/**
 * FancyMyText - Text Style Transformations
 * All Unicode text transformations and style metadata
 */

import { TextStyle, StyleCategory } from './types';

// Character maps for styles that don't follow a simple offset pattern
const SMALL_CAPS_MAP: Record<string, string> = {
  'a': 'ᴀ', 'b': 'ʙ', 'c': 'ᴄ', 'd': 'ᴅ', 'e': 'ᴇ', 'f': 'ғ', 'g': 'ɢ', 'h': 'ʜ',
  'i': 'ɪ', 'j': 'ᴊ', 'k': 'ᴋ', 'l': 'ʟ', 'm': 'ᴍ', 'n': 'ɴ', 'o': 'ᴏ', 'p': 'ᴘ',
  'q': 'ǫ', 'r': 'ʀ', 's': 's', 't': 'ᴛ', 'u': 'ᴜ', 'v': 'ᴠ', 'w': 'ᴡ', 'x': 'x',
  'y': 'ʏ', 'z': 'ᴢ'
};

const SUPERSCRIPT_MAP: Record<string, string> = {
  'a': 'ᵃ', 'b': 'ᵇ', 'c': 'ᶜ', 'd': 'ᵈ', 'e': 'ᵉ', 'f': 'ᶠ', 'g': 'ᵍ', 'h': 'ʰ',
  'i': 'ⁱ', 'j': 'ʲ', 'k': 'ᵏ', 'l': 'ˡ', 'm': 'ᵐ', 'n': 'ⁿ', 'o': 'ᵒ', 'p': 'ᵖ',
  'q': 'q', 'r': 'ʳ', 's': 'ˢ', 't': 'ᵗ', 'u': 'ᵘ', 'v': 'ᵛ', 'w': 'ʷ', 'x': 'ˣ',
  'y': 'ʸ', 'z': 'ᶻ',
  'A': 'ᴬ', 'B': 'ᴮ', 'C': 'ᶜ', 'D': 'ᴰ', 'E': 'ᴱ', 'F': 'ᶠ', 'G': 'ᴳ', 'H': 'ᴴ',
  'I': 'ᴵ', 'J': 'ᴶ', 'K': 'ᴷ', 'L': 'ᴸ', 'M': 'ᴹ', 'N': 'ᴺ', 'O': 'ᴼ', 'P': 'ᴾ',
  'Q': 'Q', 'R': 'ᴿ', 'S': 'ˢ', 'T': 'ᵀ', 'U': 'ᵁ', 'V': 'ⱽ', 'W': 'ᵂ', 'X': 'ˣ',
  'Y': 'ʸ', 'Z': 'ᶻ',
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
};

const SUBSCRIPT_MAP: Record<string, string> = {
  'a': 'ₐ', 'e': 'ₑ', 'h': 'ₕ', 'i': 'ᵢ', 'j': 'ⱼ', 'k': 'ₖ', 'l': 'ₗ', 'm': 'ₘ',
  'n': 'ₙ', 'o': 'ₒ', 'p': 'ₚ', 'r': 'ᵣ', 's': 'ₛ', 't': 'ₜ', 'u': 'ᵤ', 'v': 'ᵥ',
  'x': 'ₓ',
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
};

const UPSIDE_DOWN_MAP: Record<string, string> = {
  'a': 'ɐ', 'b': 'q', 'c': 'ɔ', 'd': 'p', 'e': 'ǝ', 'f': 'ɟ', 'g': 'ƃ', 'h': 'ɥ',
  'i': 'ᴉ', 'j': 'ɾ', 'k': 'ʞ', 'l': 'l', 'm': 'ɯ', 'n': 'u', 'o': 'o', 'p': 'd',
  'q': 'b', 'r': 'ɹ', 's': 's', 't': 'ʇ', 'u': 'n', 'v': 'ʌ', 'w': 'ʍ', 'x': 'x',
  'y': 'ʎ', 'z': 'z',
  'A': '∀', 'B': 'q', 'C': 'Ɔ', 'D': 'p', 'E': 'Ǝ', 'F': 'Ⅎ', 'G': '⅁', 'H': 'H',
  'I': 'I', 'J': 'ſ', 'K': 'ʞ', 'L': '˥', 'M': 'W', 'N': 'N', 'O': 'O', 'P': 'Ԁ',
  'Q': 'Q', 'R': 'ɹ', 'S': 'S', 'T': '⊥', 'U': '∩', 'V': 'Λ', 'W': 'M', 'X': 'X',
  'Y': '⅄', 'Z': 'Z',
  '1': 'Ɩ', '2': 'ᄅ', '3': 'Ɛ', '4': 'ㄣ', '5': 'ϛ', '6': '9', '7': 'ㄥ', '8': '8', '9': '6', '0': '0',
  '.': '˙', ',': "'", "'": ',', '"': ',,', '!': '¡', '?': '¿', '[': ']', ']': '[',
  '(': ')', ')': '(', '{': '}', '}': '{', '<': '>', '>': '<', '_': '‾', ';': '؛',
  '&': '⅋'
};

const MIRROR_MAP: Record<string, string> = {
  'a': 'ɒ', 'b': 'd', 'c': 'ɔ', 'd': 'b', 'e': 'ɘ', 'f': 'ꟻ', 'g': 'ǫ', 'h': 'ʜ',
  'i': 'i', 'j': 'ꞁ', 'k': 'ʞ', 'l': 'l', 'm': 'm', 'n': 'ᴎ', 'o': 'o', 'p': 'q',
  'q': 'p', 'r': 'ɿ', 's': 'ꙅ', 't': 'ƚ', 'u': 'u', 'v': 'v', 'w': 'w', 'x': 'x',
  'y': 'y', 'z': 'ꙃ',
  'A': 'A', 'B': 'ᙠ', 'C': 'Ɔ', 'D': 'ᗡ', 'E': 'Ǝ', 'F': 'ꟻ', 'G': 'Ꭾ', 'H': 'H',
  'I': 'I', 'J': 'Ⴑ', 'K': 'ꓘ', 'L': '⅃', 'M': 'M', 'N': 'ᴎ', 'O': 'O', 'P': 'ꟼ',
  'Q': 'Ọ', 'R': 'ᴙ', 'S': 'Ꙅ', 'T': 'T', 'U': 'U', 'V': 'V', 'W': 'W', 'X': 'X',
  'Y': 'Y', 'Z': 'Ꙃ',
  '1': '1', '2': '2', '3': 'Ɛ', '4': 'ᔭ', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '0': '0',
  '?': '⸮', '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{', '<': '>', '>': '<',
  '/': '\\', '\\': '/'
};

// Mathematical Script (light cursive) - has holes in Unicode, need explicit mapping
const CURSIVE_LIGHT_MAP: Record<string, string> = {
  'A': '𝒜', 'B': 'ℬ', 'C': '𝒞', 'D': '𝒟', 'E': 'ℰ', 'F': 'ℱ', 'G': '𝒢', 'H': 'ℋ',
  'I': 'ℐ', 'J': '𝒥', 'K': '𝒦', 'L': 'ℒ', 'M': 'ℳ', 'N': '𝒩', 'O': '𝒪', 'P': '𝒫',
  'Q': '𝒬', 'R': 'ℛ', 'S': '𝒮', 'T': '𝒯', 'U': '𝒰', 'V': '𝒱', 'W': '𝒲', 'X': '𝒳',
  'Y': '𝒴', 'Z': '𝒵',
  'a': '𝒶', 'b': '𝒷', 'c': '𝒸', 'd': '𝒹', 'e': 'ℯ', 'f': '𝒻', 'g': 'ℊ', 'h': '𝒽',
  'i': '𝒾', 'j': '𝒿', 'k': '𝓀', 'l': '𝓁', 'm': '𝓂', 'n': '𝓃', 'o': 'ℴ', 'p': '𝓅',
  'q': '𝓆', 'r': '𝓇', 's': '𝓈', 't': '𝓉', 'u': '𝓊', 'v': '𝓋', 'w': '𝓌', 'x': '𝓍',
  'y': '𝓎', 'z': '𝓏'
};

// Mathematical Italic - has hole at lowercase h
const ITALIC_SERIF_MAP: Record<string, string> = {
  'A': '𝐴', 'B': '𝐵', 'C': '𝐶', 'D': '𝐷', 'E': '𝐸', 'F': '𝐹', 'G': '𝐺', 'H': '𝐻',
  'I': '𝐼', 'J': '𝐽', 'K': '𝐾', 'L': '𝐿', 'M': '𝑀', 'N': '𝑁', 'O': '𝑂', 'P': '𝑃',
  'Q': '𝑄', 'R': '𝑅', 'S': '𝑆', 'T': '𝑇', 'U': '𝑈', 'V': '𝑉', 'W': '𝑊', 'X': '𝑋',
  'Y': '𝑌', 'Z': '𝑍',
  'a': '𝑎', 'b': '𝑏', 'c': '𝑐', 'd': '𝑑', 'e': '𝑒', 'f': '𝑓', 'g': '𝑔', 'h': 'ℎ',
  'i': '𝑖', 'j': '𝑗', 'k': '𝑘', 'l': '𝑙', 'm': '𝑚', 'n': '𝑛', 'o': '𝑜', 'p': '𝑝',
  'q': '𝑞', 'r': '𝑟', 's': '𝑠', 't': '𝑡', 'u': '𝑢', 'v': '𝑣', 'w': '𝑤', 'x': '𝑥',
  'y': '𝑦', 'z': '𝑧'
};

// Mathematical Fraktur - has holes at C, H, I, R, Z uppercase
const FRAKTUR_MAP: Record<string, string> = {
  'A': '𝔄', 'B': '𝔅', 'C': 'ℭ', 'D': '𝔇', 'E': '𝔈', 'F': '𝔉', 'G': '𝔊', 'H': 'ℌ',
  'I': 'ℑ', 'J': '𝔍', 'K': '𝔎', 'L': '𝔏', 'M': '𝔐', 'N': '𝔑', 'O': '𝔒', 'P': '𝔓',
  'Q': '𝔔', 'R': 'ℜ', 'S': '𝔖', 'T': '𝔗', 'U': '𝔘', 'V': '𝔙', 'W': '𝔚', 'X': '𝔛',
  'Y': '𝔜', 'Z': 'ℨ',
  'a': '𝔞', 'b': '𝔟', 'c': '𝔠', 'd': '𝔡', 'e': '𝔢', 'f': '𝔣', 'g': '𝔤', 'h': '𝔥',
  'i': '𝔦', 'j': '𝔧', 'k': '𝔨', 'l': '𝔩', 'm': '𝔪', 'n': '𝔫', 'o': '𝔬', 'p': '𝔭',
  'q': '𝔮', 'r': '𝔯', 's': '𝔰', 't': '𝔱', 'u': '𝔲', 'v': '𝔳', 'w': '𝔴', 'x': '𝔵',
  'y': '𝔶', 'z': '𝔷'
};

// Runic/Elder Futhark mapping
const RUNIC_MAP: Record<string, string> = {
  'a': 'ᚨ', 'b': 'ᛒ', 'c': 'ᚲ', 'd': 'ᛞ', 'e': 'ᛖ', 'f': 'ᚠ', 'g': 'ᚷ', 'h': 'ᚺ',
  'i': 'ᛁ', 'j': 'ᛃ', 'k': 'ᚲ', 'l': 'ᛚ', 'm': 'ᛗ', 'n': 'ᚾ', 'o': 'ᛟ', 'p': 'ᛈ',
  'q': 'ᛩ', 'r': 'ᚱ', 's': 'ᛊ', 't': 'ᛏ', 'u': 'ᚢ', 'v': 'ᚡ', 'w': 'ᚹ', 'x': 'ᛪ',
  'y': 'ᚤ', 'z': 'ᛉ',
  'A': 'ᚨ', 'B': 'ᛒ', 'C': 'ᚲ', 'D': 'ᛞ', 'E': 'ᛖ', 'F': 'ᚠ', 'G': 'ᚷ', 'H': 'ᚺ',
  'I': 'ᛁ', 'J': 'ᛃ', 'K': 'ᚲ', 'L': 'ᛚ', 'M': 'ᛗ', 'N': 'ᚾ', 'O': 'ᛟ', 'P': 'ᛈ',
  'Q': 'ᛩ', 'R': 'ᚱ', 'S': 'ᛊ', 'T': 'ᛏ', 'U': 'ᚢ', 'V': 'ᚡ', 'W': 'ᚹ', 'X': 'ᛪ',
  'Y': 'ᚤ', 'Z': 'ᛉ',
  '0': '᛫', '1': 'ᛁ', '2': 'ᛁᛁ', '3': 'ᛁᛁᛁ', '4': 'ᛁᚡ', '5': 'ᚡ', '6': 'ᚡᛁ', '7': 'ᚡᛁᛁ', '8': 'ᚡᛁᛁᛁ', '9': 'ᛁᛪ'
};

// Alien/Geometric script (Tifinagh-inspired)
const ALIEN_MAP: Record<string, string> = {
  'a': 'ⴰ', 'b': 'ⴱ', 'c': 'ⵛ', 'd': 'ⴷ', 'e': 'ⵦ', 'f': 'ⴼ', 'g': 'ⴳ', 'h': 'ⵀ',
  'i': 'ⵉ', 'j': 'ⵊ', 'k': 'ⴾ', 'l': 'ⵍ', 'm': 'ⵎ', 'n': 'ⵏ', 'o': 'ⵄ', 'p': 'ⵒ',
  'q': 'ⵈ', 'r': 'ⵔ', 's': 'ⵙ', 't': 'ⵜ', 'u': 'ⵓ', 'v': 'ⵖ', 'w': 'ⵡ', 'x': 'ⵅ',
  'y': 'ⵢ', 'z': 'ⵣ',
  'A': 'ⴰ', 'B': 'ⴱ', 'C': 'ⵛ', 'D': 'ⴷ', 'E': 'ⵦ', 'F': 'ⴼ', 'G': 'ⴳ', 'H': 'ⵀ',
  'I': 'ⵉ', 'J': 'ⵊ', 'K': 'ⴾ', 'L': 'ⵍ', 'M': 'ⵎ', 'N': 'ⵏ', 'O': 'ⵄ', 'P': 'ⵒ',
  'Q': 'ⵈ', 'R': 'ⵔ', 'S': 'ⵙ', 'T': 'ⵜ', 'U': 'ⵓ', 'V': 'ⵖ', 'W': 'ⵡ', 'X': 'ⵅ',
  'Y': 'ⵢ', 'Z': 'ⵣ'
};

// Matrix/Hacker style mapping
const MATRIX_MAP: Record<string, string> = {
  'a': '∀', 'b': 'ß', 'c': '¢', 'd': 'Ð', 'e': '€', 'f': '₣', 'g': 'ǥ', 'h': 'ħ',
  'i': '¡', 'j': 'ʝ', 'k': 'ʞ', 'l': '₤', 'm': 'ɯ', 'n': 'η', 'o': 'Ø', 'p': 'þ',
  'q': 'Ҩ', 'r': 'Я', 's': '$', 't': '┼', 'u': 'µ', 'v': 'ν', 'w': 'ψ', 'x': '×',
  'y': '¥', 'z': 'ƶ',
  'A': '∀', 'B': 'ß', 'C': '¢', 'D': 'Ð', 'E': '€', 'F': '₣', 'G': 'Ǥ', 'H': 'Ħ',
  'I': '¡', 'J': 'ʝ', 'K': 'Ҝ', 'L': '₤', 'M': 'Ɯ', 'N': 'η', 'O': 'Ø', 'P': 'Þ',
  'Q': 'Ҩ', 'R': 'Я', 'S': '$', 'T': '┼', 'U': 'µ', 'V': 'ν', 'W': 'Ψ', 'X': '×',
  'Y': '¥', 'Z': 'Ƶ',
  '0': 'Ø', '1': '①', '2': '②', '3': '③', '4': '④', '5': '⑤', '6': '⑥', '7': '⑦', '8': '⑧', '9': '⑨'
};

// Leet speak map
const LEET_MAP: Record<string, string> = {
  'a': '4', 'A': '4',
  'b': '8', 'B': '8',
  'c': '(', 'C': '(',
  'e': '3', 'E': '3',
  'g': '6', 'G': '6',
  'h': '#', 'H': '#',
  'i': '1', 'I': '1',
  'l': '1', 'L': '1',
  'o': '0', 'O': '0',
  's': '5', 'S': '5',
  't': '7', 'T': '7',
  'z': '2', 'Z': '2'
};

// Morse code map
const MORSE_MAP: Record<string, string> = {
  'a': '.-', 'b': '-...', 'c': '-.-.', 'd': '-..', 'e': '.', 'f': '..-.', 'g': '--.', 'h': '....',
  'i': '..', 'j': '.---', 'k': '-.-', 'l': '.-..', 'm': '--', 'n': '-.', 'o': '---', 'p': '.--.',
  'q': '--.-', 'r': '.-.', 's': '...', 't': '-', 'u': '..-', 'v': '...-', 'w': '.--', 'x': '-..-',
  'y': '-.--', 'z': '--..',
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.', 'G': '--.', 'H': '....',
  'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..', 'M': '--', 'N': '-.', 'O': '---', 'P': '.--.',
  'Q': '--.-', 'R': '.-.', 'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
  'Y': '-.--', 'Z': '--..',
  '0': '-----', '1': '.----', '2': '..---', '3': '...--', '4': '....-',
  '5': '.....', '6': '-....', '7': '--...', '8': '---..', '9': '----.',
  ' ': '/'
};

// Braille map (Unicode braille patterns)
const BRAILLE_MAP: Record<string, string> = {
  'a': '⠁', 'b': '⠃', 'c': '⠉', 'd': '⠙', 'e': '⠑', 'f': '⠋', 'g': '⠛', 'h': '⠓',
  'i': '⠊', 'j': '⠚', 'k': '⠅', 'l': '⠇', 'm': '⠍', 'n': '⠝', 'o': '⠕', 'p': '⠏',
  'q': '⠟', 'r': '⠗', 's': '⠎', 't': '⠞', 'u': '⠥', 'v': '⠧', 'w': '⠺', 'x': '⠭',
  'y': '⠽', 'z': '⠵',
  'A': '⠁', 'B': '⠃', 'C': '⠉', 'D': '⠙', 'E': '⠑', 'F': '⠋', 'G': '⠛', 'H': '⠓',
  'I': '⠊', 'J': '⠚', 'K': '⠅', 'L': '⠇', 'M': '⠍', 'N': '⠝', 'O': '⠕', 'P': '⠏',
  'Q': '⠟', 'R': '⠗', 'S': '⠎', 'T': '⠞', 'U': '⠥', 'V': '⠧', 'W': '⠺', 'X': '⠭',
  'Y': '⠽', 'Z': '⠵',
  '0': '⠼⠚', '1': '⠼⠁', '2': '⠼⠃', '3': '⠼⠉', '4': '⠼⠙',
  '5': '⠼⠑', '6': '⠼⠋', '7': '⠼⠛', '8': '⠼⠓', '9': '⠼⠊',
  ' ': '⠀'
};

// Mathematical Double-Struck - has holes at C, H, N, P, Q, R, Z uppercase
const DOUBLE_STRUCK_MAP: Record<string, string> = {
  'A': '𝔸', 'B': '𝔹', 'C': 'ℂ', 'D': '𝔻', 'E': '𝔼', 'F': '𝔽', 'G': '𝔾', 'H': 'ℍ',
  'I': '𝕀', 'J': '𝕁', 'K': '𝕂', 'L': '𝕃', 'M': '𝕄', 'N': 'ℕ', 'O': '𝕆', 'P': 'ℙ',
  'Q': 'ℚ', 'R': 'ℝ', 'S': '𝕊', 'T': '𝕋', 'U': '𝕌', 'V': '𝕍', 'W': '𝕎', 'X': '𝕏',
  'Y': '𝕐', 'Z': 'ℤ',
  'a': '𝕒', 'b': '𝕓', 'c': '𝕔', 'd': '𝕕', 'e': '𝕖', 'f': '𝕗', 'g': '𝕘', 'h': '𝕙',
  'i': '𝕚', 'j': '𝕛', 'k': '𝕜', 'l': '𝕝', 'm': '𝕞', 'n': '𝕟', 'o': '𝕠', 'p': '𝕡',
  'q': '𝕢', 'r': '𝕣', 's': '𝕤', 't': '𝕥', 'u': '𝕦', 'v': '𝕧', 'w': '𝕨', 'x': '𝕩',
  'y': '𝕪', 'z': '𝕫',
  '0': '𝟘', '1': '𝟙', '2': '𝟚', '3': '𝟛', '4': '𝟜', '5': '𝟝', '6': '𝟞', '7': '𝟟', '8': '𝟠', '9': '𝟡'
};

// Zalgo combining characters
const ZALGO_UP: string[] = [
  '\u030d', '\u030e', '\u0304', '\u0305', '\u033f', '\u0311', '\u0306', '\u0310',
  '\u0352', '\u0357', '\u0351', '\u0307', '\u0308', '\u030a', '\u0342', '\u0343',
  '\u0344', '\u034a', '\u034b', '\u034c', '\u0303', '\u0302', '\u030c', '\u0350',
  '\u0300', '\u0301', '\u030b', '\u030f', '\u0312', '\u0313', '\u0314', '\u033d',
  '\u0309', '\u0363', '\u0364', '\u0365', '\u0366', '\u0367', '\u0368', '\u0369',
  '\u036a', '\u036b', '\u036c', '\u036d', '\u036e', '\u036f', '\u033e', '\u035b'
];

const ZALGO_MID: string[] = [
  '\u0315', '\u031b', '\u0340', '\u0341', '\u0358', '\u0321', '\u0322', '\u0327',
  '\u0328', '\u0334', '\u0335', '\u0336', '\u034f', '\u035c', '\u035d', '\u035e',
  '\u035f', '\u0360', '\u0362', '\u0338', '\u0337', '\u0361', '\u0489'
];

const ZALGO_DOWN: string[] = [
  '\u0316', '\u0317', '\u0318', '\u0319', '\u031c', '\u031d', '\u031e', '\u031f',
  '\u0320', '\u0324', '\u0325', '\u0326', '\u0329', '\u032a', '\u032b', '\u032c',
  '\u032d', '\u032e', '\u032f', '\u0330', '\u0331', '\u0332', '\u0333', '\u0339',
  '\u033a', '\u033b', '\u033c', '\u0345', '\u0347', '\u0348', '\u0349', '\u034d',
  '\u034e', '\u0353', '\u0354', '\u0355', '\u0356', '\u0359', '\u035a', '\u0323'
];

// Helper functions for Unicode transformations
const transformWithOffset = (text: string, upperStart: number, lowerStart: number, digitStart: number | null = null): string => {
  return text.split('').map(char => {
    const code = char.charCodeAt(0);
    if (code >= 65 && code <= 90) {
      return String.fromCodePoint(upperStart + (code - 65));
    }
    if (code >= 97 && code <= 122) {
      return String.fromCodePoint(lowerStart + (code - 97));
    }
    if (digitStart && code >= 48 && code <= 57) {
      return String.fromCodePoint(digitStart + (code - 48));
    }
    return char;
  }).join('');
};

const transformWithMap = (text: string, map: Record<string, string>, preserveCase: boolean = false): string => {
  return text.split('').map(char => {
    if (preserveCase) {
      return map[char] || char;
    }
    const lower = char.toLowerCase();
    return map[lower] || map[char] || char;
  }).join('');
};

// Text style definitions
export const TEXT_STYLES: TextStyle[] = [
  // === BOLD STYLES ===
  {
    id: 'bold',
    name: 'Bold',
    slug: 'bold',
    category: 'basic',
    example: '𝗕𝗼𝗹𝗱',
    description: 'Transform your text into bold Unicode characters that work everywhere.',
    keywords: ['bold text generator', 'bold font', 'bold letters', 'texto en negrita'],
    transform: (text: string): string => transformWithOffset(text, 0x1D5D4, 0x1D5EE, 0x1D7EC),
    related: ['bold-italic', 'bold-serif', 'bold-fraktur'],
    faq: [
      { q: 'How does bold text work?', a: 'Bold text uses Mathematical Sans-Serif Bold Unicode characters (U+1D5D4 onwards) that display as bold on all platforms.' },
      { q: 'Can I use bold text on Instagram?', a: 'Yes! Bold Unicode text works in Instagram bios, captions, comments, and stories.' },
      { q: 'Why use bold text?', a: 'Bold text helps emphasize important words, make headlines stand out, and grab attention on social media.' }
    ]
  },
  {
    id: 'bold-serif',
    name: 'Bold Serif',
    slug: 'bold-serif',
    category: 'basic',
    example: '𝐁𝐨𝐥𝐝',
    description: 'Classic bold serif style with traditional typography aesthetics.',
    keywords: ['bold serif', 'serif font generator', 'classic bold'],
    transform: (text: string): string => transformWithOffset(text, 0x1D400, 0x1D41A, 0x1D7CE),
    related: ['bold', 'italic-serif', 'bold-italic-serif'],
    faq: [
      { q: 'What is bold serif?', a: 'Bold serif uses Mathematical Bold characters with traditional serif styling, giving a classic typographic look.' },
      { q: 'When should I use bold serif?', a: 'Use bold serif for formal content, professional bios, or when you want a traditional, authoritative appearance.' }
    ]
  },

  // === ITALIC STYLES ===
  {
    id: 'italic',
    name: 'Italic',
    slug: 'italic',
    category: 'basic',
    example: '𝘐𝘵𝘢𝘭𝘪𝘤',
    description: 'Create elegant italic text that adds emphasis and style to your content.',
    keywords: ['italic text generator', 'italic font', 'cursiva', 'italique'],
    transform: (text: string): string => transformWithOffset(text, 0x1D608, 0x1D622),
    related: ['bold-italic', 'italic-serif', 'cursive'],
    faq: [
      { q: 'How do I make italic text?', a: 'Simply type your text above and it will be converted to italic Unicode characters automatically.' },
      { q: 'Does italic text work on Twitter?', a: 'Yes! Italic Unicode characters work on Twitter/X, Facebook, Instagram, and most social platforms.' }
    ]
  },
  {
    id: 'italic-serif',
    name: 'Italic Serif',
    slug: 'italic-serif',
    category: 'basic',
    example: '𝐼𝑡𝑎𝑙𝑖𝑐',
    description: 'Traditional italic serif styling for elegant, classic text.',
    keywords: ['italic serif', 'classic italic', 'serif italic generator'],
    transform: (text: string): string => transformWithMap(text, ITALIC_SERIF_MAP, true),
    related: ['italic', 'bold-italic-serif', 'cursive'],
    faq: [
      { q: 'What is italic serif?', a: 'Italic serif combines italicized slant with traditional serif letterforms for a classic, elegant look.' }
    ]
  },

  // === BOLD ITALIC ===
  {
    id: 'bold-italic',
    name: 'Bold Italic',
    slug: 'bold-italic',
    category: 'basic',
    example: '𝘽𝙤𝙡𝙙 𝙄𝙩𝙖𝙡𝙞𝙘',
    description: 'Combine bold weight with italic style for maximum emphasis.',
    keywords: ['bold italic text', 'bold italic generator', 'negrita cursiva'],
    transform: (text: string): string => transformWithOffset(text, 0x1D63C, 0x1D656),
    related: ['bold', 'italic', 'bold-italic-serif'],
    faq: [
      { q: 'When should I use bold italic?', a: 'Use bold italic for strong emphasis, important announcements, or when you want text to really stand out.' }
    ]
  },
  {
    id: 'bold-italic-serif',
    name: 'Bold Italic Serif',
    slug: 'bold-italic-serif',
    category: 'basic',
    example: '𝑩𝒐𝒍𝒅 𝑰𝒕𝒂𝒍𝒊𝒄',
    description: 'Traditional bold italic with classic serif typography.',
    keywords: ['bold italic serif', 'classic bold italic'],
    transform: (text: string): string => transformWithOffset(text, 0x1D468, 0x1D482),
    related: ['bold-italic', 'bold-serif', 'italic-serif'],
    faq: [
      { q: 'What makes bold italic serif different?', a: 'Bold italic serif combines three typographic features: bold weight, italic slant, and classic serif details.' }
    ]
  },

  // === SCRIPT/CURSIVE ===
  {
    id: 'cursive',
    name: 'Cursive',
    slug: 'cursive',
    category: 'decorative',
    example: '𝓒𝓾𝓻𝓼𝓲𝓿𝓮',
    description: 'Beautiful cursive script text that adds elegance and flair.',
    keywords: ['cursive text generator', 'script font', 'fancy cursive', 'letras cursivas', 'écriture cursive'],
    transform: (text: string): string => transformWithOffset(text, 0x1D4D0, 0x1D4EA),
    related: ['cursive-light', 'italic', 'fancy'],
    faq: [
      { q: 'How do I make cursive text?', a: 'Type your text in the box above to instantly convert it to beautiful cursive Unicode characters.' },
      { q: 'Can I use cursive on Instagram?', a: 'Yes! Cursive Unicode text works perfectly in Instagram bios, captions, and comments.' },
      { q: 'Is this real cursive font?', a: 'These are Unicode Mathematical Script Bold characters that look like cursive and work everywhere text is supported.' }
    ]
  },
  {
    id: 'cursive-light',
    name: 'Cursive Light',
    slug: 'cursive-light',
    category: 'decorative',
    example: '𝒞𝓊𝓇𝓈𝒾𝓋ℯ',
    description: 'Lighter, more delicate cursive script styling.',
    keywords: ['light cursive', 'elegant script', 'fancy text light'],
    transform: (text: string): string => transformWithMap(text, CURSIVE_LIGHT_MAP, true),
    related: ['cursive', 'italic', 'fancy'],
    faq: [
      { q: 'What is cursive light?', a: 'Cursive light uses thinner Mathematical Script characters for a more delicate, elegant appearance.' }
    ]
  },

  // === FRAKTUR/GOTHIC ===
  {
    id: 'fraktur',
    name: 'Fraktur',
    slug: 'fraktur',
    category: 'decorative',
    example: '𝔉𝔯𝔞𝔨𝔱𝔲𝔯',
    description: 'Gothic blackletter style inspired by medieval German calligraphy.',
    keywords: ['fraktur text', 'gothic font generator', 'blackletter', 'old english font', 'medieval text'],
    transform: (text: string): string => transformWithMap(text, FRAKTUR_MAP, true),
    related: ['bold-fraktur', 'medieval', 'gothic'],
    faq: [
      { q: 'What is Fraktur text?', a: 'Fraktur is a Gothic blackletter typeface that was widely used in Germany from the 16th to 20th century.' },
      { q: 'Can I use Fraktur on social media?', a: 'Yes! Fraktur Unicode characters work on Instagram, Twitter, Facebook, and all major platforms.' }
    ]
  },
  {
    id: 'bold-fraktur',
    name: 'Bold Fraktur',
    slug: 'bold-fraktur',
    category: 'decorative',
    example: '𝕭𝖔𝖑𝖉 𝕱𝖗𝖆𝖐𝖙𝖚𝖗',
    description: 'Heavy bold Gothic blackletter for dramatic effect.',
    keywords: ['bold fraktur', 'bold gothic', 'heavy blackletter'],
    transform: (text: string): string => transformWithOffset(text, 0x1D56C, 0x1D586),
    related: ['fraktur', 'gothic', 'medieval'],
    faq: [
      { q: 'When should I use bold Fraktur?', a: 'Bold Fraktur is perfect for metal band aesthetics, gothic themes, or dramatic headers.' }
    ]
  },

  // === SPECIAL STYLES ===
  {
    id: 'double-struck',
    name: 'Double Struck',
    slug: 'double-struck',
    category: 'decorative',
    example: '𝔻𝕠𝕦𝕓𝕝𝕖',
    description: 'Outlined characters also known as "blackboard bold" from mathematics.',
    keywords: ['double struck text', 'outlined font', 'blackboard bold', 'hollow letters'],
    transform: (text: string): string => transformWithMap(text, DOUBLE_STRUCK_MAP, true),
    related: ['bubble', 'square', 'monospace'],
    faq: [
      { q: 'What is double struck text?', a: 'Double struck (blackboard bold) characters have a hollow, outlined appearance originally used in mathematical notation.' }
    ]
  },
  {
    id: 'monospace',
    name: 'Monospace',
    slug: 'monospace',
    category: 'basic',
    example: '𝙼𝚘𝚗𝚘𝚜𝚙𝚊𝚌𝚎',
    description: 'Fixed-width typewriter style text for code-like aesthetics.',
    keywords: ['monospace text', 'typewriter font', 'code font', 'fixed width'],
    transform: (text: string): string => transformWithOffset(text, 0x1D670, 0x1D68A, 0x1D7F6),
    related: ['double-struck', 'square', 'bold'],
    faq: [
      { q: 'What is monospace text?', a: 'Monospace fonts have equal width for every character, like old typewriters or code editors.' },
      { q: 'When should I use monospace?', a: 'Use monospace for code snippets, technical content, or retro typewriter aesthetics.' }
    ]
  },

  // === BUBBLE & SQUARE ===
  {
    id: 'bubble',
    name: 'Bubble',
    slug: 'bubble',
    category: 'enclosed',
    example: 'Ⓑⓤⓑⓑⓛⓔ',
    description: 'Letters enclosed in circles for a fun, bubbly appearance.',
    keywords: ['bubble text generator', 'circle letters', 'enclosed text', 'letras circulares'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 65 && code <= 90) return String.fromCodePoint(0x24B6 + (code - 65));
        if (code >= 97 && code <= 122) return String.fromCodePoint(0x24D0 + (code - 97));
        if (code >= 48 && code <= 57) {
          if (code === 48) return '\u24EA';
          return String.fromCodePoint(0x2460 + (code - 49));
        }
        return char;
      }).join('');
    },
    related: ['bubble-filled', 'square', 'square-filled'],
    faq: [
      { q: 'How do bubble letters work?', a: 'Bubble text uses Unicode Enclosed Alphanumerics where each letter is inside a circle.' },
      { q: 'Can I use bubble text on Instagram?', a: 'Yes! Bubble letters work perfectly on Instagram, TikTok, Twitter, and all social platforms.' }
    ]
  },
  {
    id: 'bubble-filled',
    name: 'Bubble Filled',
    slug: 'bubble-filled',
    category: 'enclosed',
    example: '🅑🅤🅑🅑🅛🅔',
    description: 'Solid filled circle letters for high contrast visibility.',
    keywords: ['filled bubble text', 'solid circle letters', 'negative circled'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 65 && code <= 90) return String.fromCodePoint(0x1F150 + (code - 65));
        if (code >= 97 && code <= 122) return String.fromCodePoint(0x1F150 + (code - 97));
        return char;
      }).join('');
    },
    related: ['bubble', 'square-filled', 'square'],
    faq: [
      { q: 'What is filled bubble text?', a: 'Filled bubble uses Negative Circled Latin letters with white letters on dark backgrounds.' }
    ]
  },
  {
    id: 'square',
    name: 'Square',
    slug: 'square',
    category: 'enclosed',
    example: '🄱🄾🅇🄴🄳',
    description: 'Letters enclosed in squares for a bold, blocky look.',
    keywords: ['square text generator', 'box letters', 'squared text'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 65 && code <= 90) return String.fromCodePoint(0x1F130 + (code - 65));
        if (code >= 97 && code <= 122) return String.fromCodePoint(0x1F130 + (code - 97));
        return char;
      }).join('');
    },
    related: ['square-filled', 'bubble', 'bubble-filled'],
    faq: [
      { q: 'How does square text work?', a: 'Square text uses Squared Latin Capital Letters from the Enclosed Alphanumeric Supplement Unicode block.' }
    ]
  },
  {
    id: 'square-filled',
    name: 'Square Filled',
    slug: 'square-filled',
    category: 'enclosed',
    example: '🅂🅀🅄🄰🅁🄴',
    description: 'Solid filled square letters with high visual impact.',
    keywords: ['filled square text', 'solid box letters', 'negative squared'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 65 && code <= 90) return String.fromCodePoint(0x1F170 + (code - 65));
        if (code >= 97 && code <= 122) return String.fromCodePoint(0x1F170 + (code - 97));
        return char;
      }).join('');
    },
    related: ['square', 'bubble-filled', 'bubble'],
    faq: [
      { q: 'What is filled square text?', a: 'Filled square uses Negative Squared Latin letters with white letters on dark square backgrounds.' }
    ]
  },

  // === SIZE VARIANTS ===
  {
    id: 'small-caps',
    name: 'Small Caps',
    slug: 'small-caps',
    category: 'basic',
    example: 'sᴍᴀʟʟ ᴄᴀᴘs',
    description: 'Lowercase letters rendered as smaller capital letters.',
    keywords: ['small caps generator', 'small capital letters', 'versalitas', 'petites majuscules'],
    transform: (text: string): string => transformWithMap(text.toLowerCase(), SMALL_CAPS_MAP),
    related: ['superscript', 'subscript', 'bold'],
    faq: [
      { q: 'What are small caps?', a: 'Small caps are capital letters that are the same height as lowercase letters, commonly used in typography.' },
      { q: 'Where are small caps used?', a: 'Small caps are popular for headings, author names, acronyms, and adding subtle emphasis.' }
    ]
  },
  {
    id: 'superscript',
    name: 'Superscript',
    slug: 'superscript',
    category: 'basic',
    example: 'ˢᵘᵖᵉʳˢᶜʳⁱᵖᵗ',
    description: 'Tiny raised text perfect for exponents and annotations.',
    keywords: ['superscript generator', 'tiny text', 'raised text', 'small text generator'],
    transform: (text: string): string => transformWithMap(text, SUPERSCRIPT_MAP, true),
    related: ['subscript', 'small-caps', 'tiny'],
    faq: [
      { q: 'What is superscript?', a: 'Superscript is smaller text positioned above the normal text baseline, like exponents in math.' },
      { q: 'Can I use superscript on social media?', a: 'Yes! Superscript Unicode characters work on most social platforms for annotations and effects.' }
    ]
  },
  {
    id: 'subscript',
    name: 'Subscript',
    slug: 'subscript',
    category: 'basic',
    example: 'ₛᵤᵦₛ꜀ᵣᵢₚₜ',
    description: 'Small lowered text for chemical formulas and footnotes.',
    keywords: ['subscript generator', 'lowered text', 'chemical formula text'],
    transform: (text: string): string => transformWithMap(text.toLowerCase(), SUBSCRIPT_MAP),
    related: ['superscript', 'small-caps', 'tiny'],
    faq: [
      { q: 'What is subscript used for?', a: 'Subscript is commonly used for chemical formulas (H₂O), mathematical notation, and footnote references.' }
    ]
  },

  // === FULLWIDTH ===
  {
    id: 'fullwidth',
    name: 'Fullwidth',
    slug: 'fullwidth',
    category: 'aesthetic',
    example: 'Ｆｕｌｌｗｉｄｔｈ',
    description: 'Wide spaced aesthetic text popular in vaporwave culture.',
    keywords: ['fullwidth text', 'vaporwave text generator', 'aesthetic text', 'wide text', 'ａｅｓｔｈｅｔｉｃ'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 33 && code <= 126) {
          return String.fromCodePoint(code - 33 + 0xFF01);
        }
        if (code === 32) return '\u3000';
        return char;
      }).join('');
    },
    related: ['vaporwave', 'spaced', 'aesthetic'],
    faq: [
      { q: 'What is fullwidth text?', a: 'Fullwidth characters take up the same width as CJK (Chinese/Japanese/Korean) characters, creating a spaced aesthetic.' },
      { q: 'Why is fullwidth popular?', a: 'Fullwidth text became popular through vaporwave and aesthetic internet culture for its unique visual style.' }
    ]
  },

  // === DECORATORS ===
  {
    id: 'strikethrough',
    name: 'Strikethrough',
    slug: 'strikethrough',
    category: 'decorator',
    example: 'S̶t̶r̶i̶k̶e̶',
    description: 'Text with a line through it for corrections or dramatic effect.',
    keywords: ['strikethrough text generator', 'crossed out text', 'texto tachado', 'barré'],
    transform: (text: string): string => text.split('').map(char => char + '\u0336').join(''),
    related: ['underline', 'slash-through', 'tilde-strike'],
    faq: [
      { q: 'How does strikethrough work?', a: 'Strikethrough adds a combining long stroke overlay (U+0336) after each character.' },
      { q: 'When should I use strikethrough?', a: 'Use strikethrough for corrections, sarcasm, showing changes, or dramatic effect.' }
    ]
  },
  {
    id: 'underline',
    name: 'Underline',
    slug: 'underline',
    category: 'decorator',
    example: 'U̲n̲d̲e̲r̲l̲i̲n̲e̲',
    description: 'Text with a line underneath for emphasis.',
    keywords: ['underline text generator', 'underlined text', 'texto subrayado'],
    transform: (text: string): string => text.split('').map(char => char + '\u0332').join(''),
    related: ['strikethrough', 'double-underline', 'bold'],
    faq: [
      { q: 'How does Unicode underline work?', a: 'Unicode underline adds a combining low line (U+0332) after each character to create the underline effect.' }
    ]
  },
  {
    id: 'double-underline',
    name: 'Double Underline',
    slug: 'double-underline',
    category: 'decorator',
    example: 'D̳o̳u̳b̳l̳e̳',
    description: 'Text with double line underneath for strong emphasis.',
    keywords: ['double underline text', 'double underlined'],
    transform: (text: string): string => text.split('').map(char => char + '\u0333').join(''),
    related: ['underline', 'strikethrough', 'bold'],
    faq: [
      { q: 'What is double underline?', a: 'Double underline uses the combining double low line character (U+0333) for stronger emphasis.' }
    ]
  },
  {
    id: 'slash-through',
    name: 'Slash Through',
    slug: 'slash-through',
    category: 'decorator',
    example: 'S̷l̷a̷s̷h̷',
    description: 'Text with a diagonal slash through each character.',
    keywords: ['slash through text', 'diagonal strikethrough'],
    transform: (text: string): string => text.split('').map(char => char + '\u0337').join(''),
    related: ['strikethrough', 'tilde-strike', 'underline'],
    faq: [
      { q: 'How is slash-through different from strikethrough?', a: 'Slash-through uses a diagonal line (U+0337) while strikethrough uses a horizontal line.' }
    ]
  },
  {
    id: 'tilde-strike',
    name: 'Tilde Strike',
    slug: 'tilde-strike',
    category: 'decorator',
    example: 'T̴i̴l̴d̴e̴',
    description: 'Text with a wavy tilde line through it.',
    keywords: ['tilde strikethrough', 'wavy strikethrough'],
    transform: (text: string): string => text.split('').map(char => char + '\u0334').join(''),
    related: ['strikethrough', 'slash-through', 'wavy'],
    faq: [
      { q: 'What is tilde strike?', a: 'Tilde strike uses the combining tilde overlay (U+0334) for a wavy line effect.' }
    ]
  },

  // === NEW STYLES ===
  {
    id: 'upside-down',
    name: 'Upside Down',
    slug: 'upside-down',
    category: 'fun',
    example: 'uʍop ǝpᴉsd∩',
    description: 'Flip your text upside down for a fun, eye-catching effect.',
    keywords: ['upside down text generator', 'flip text', 'inverted text', 'texto invertido', 'texto al revés'],
    transform: (text: string): string => {
      return text.split('').map(char => UPSIDE_DOWN_MAP[char] || char).reverse().join('');
    },
    related: ['mirror', 'reversed', 'zalgo'],
    faq: [
      { q: 'How does upside down text work?', a: 'Upside down text uses special Unicode characters that look like flipped versions of regular letters, then reverses the order.' },
      { q: 'Can I use upside down text on social media?', a: 'Yes! Upside down Unicode text works on Instagram, Twitter, Facebook, TikTok, and most platforms.' },
      { q: 'Why is upside down text popular?', a: 'It\'s a fun way to grab attention, create visual interest, and stand out in comments and bios.' }
    ]
  },
  {
    id: 'mirror',
    name: 'Mirror',
    slug: 'mirror',
    category: 'fun',
    example: 'ɿoɿɿiM',
    description: 'Reverse and mirror your text for a reflected effect.',
    keywords: ['mirror text generator', 'reversed text', 'backwards text', 'texto espejo'],
    transform: (text: string): string => {
      return text.split('').map(char => MIRROR_MAP[char] || char).reverse().join('');
    },
    related: ['upside-down', 'reversed', 'zalgo'],
    faq: [
      { q: 'How does mirror text work?', a: 'Mirror text replaces characters with horizontally flipped Unicode equivalents and reverses the string.' },
      { q: 'What is mirror text used for?', a: 'Mirror text is great for creative bios, puzzles, artistic effects, and grabbing attention.' }
    ]
  },
  {
    id: 'reversed',
    name: 'Reversed',
    slug: 'reversed',
    category: 'fun',
    example: 'desreveR',
    description: 'Simply reverse the order of your text characters.',
    keywords: ['reverse text generator', 'backwards text', 'texto al revés'],
    transform: (text: string): string => text.split('').reverse().join(''),
    related: ['mirror', 'upside-down', 'zalgo'],
    faq: [
      { q: 'What is reversed text?', a: 'Reversed text simply displays characters in reverse order without changing the characters themselves.' }
    ]
  },
  {
    id: 'zalgo',
    name: 'Zalgo',
    slug: 'zalgo',
    category: 'fun',
    example: 'Z̷̨̗͊a̶̯͑l̷͇̀g̸̱͗o̷̜͝',
    description: 'Glitchy, cursed text with chaotic diacritical marks.',
    keywords: ['zalgo text generator', 'glitch text', 'cursed text', 'creepy text', 'corrupted text'],
    transform: (text: string, intensity: string = 'medium'): string => {
      const levels: Record<string, number> = { light: 2, medium: 5, heavy: 10 };
      const count = levels[intensity] || 5;

      return text.split('').map(char => {
        if (char === ' ') return char;
        let result = char;
        for (let i = 0; i < count; i++) {
          if (Math.random() > 0.5) result += ZALGO_UP[Math.floor(Math.random() * ZALGO_UP.length)];
          if (Math.random() > 0.5) result += ZALGO_MID[Math.floor(Math.random() * ZALGO_MID.length)];
          if (Math.random() > 0.5) result += ZALGO_DOWN[Math.floor(Math.random() * ZALGO_DOWN.length)];
        }
        return result;
      }).join('');
    },
    related: ['glitch', 'creepy', 'upside-down'],
    faq: [
      { q: 'What is Zalgo text?', a: 'Zalgo text uses combining diacritical marks stacked on characters to create a glitchy, corrupted appearance.' },
      { q: 'Where does Zalgo come from?', a: 'Zalgo originated from internet horror culture and creepypasta, named after a fictional entity.' },
      { q: 'Can I use Zalgo on social media?', a: 'Yes, but use sparingly as heavy Zalgo can be hard to read and may affect accessibility.' }
    ]
  },
  {
    id: 'zalgo-light',
    name: 'Zalgo Light',
    slug: 'zalgo-light',
    category: 'fun',
    example: 'Z̈ạl̄ḡo̊',
    description: 'Subtle glitch effect with fewer diacritical marks.',
    keywords: ['light zalgo', 'subtle glitch text', 'mild cursed text'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        if (char === ' ') return char;
        let result = char;
        if (Math.random() > 0.5) result += ZALGO_UP[Math.floor(Math.random() * ZALGO_UP.length)];
        if (Math.random() > 0.7) result += ZALGO_DOWN[Math.floor(Math.random() * ZALGO_DOWN.length)];
        return result;
      }).join('');
    },
    related: ['zalgo', 'zalgo-heavy', 'glitch'],
    faq: [
      { q: 'What is Zalgo light?', a: 'Zalgo light uses fewer combining marks for a subtle glitch effect that remains readable.' }
    ]
  },
  {
    id: 'zalgo-heavy',
    name: 'Zalgo Heavy',
    slug: 'zalgo-heavy',
    category: 'fun',
    example: 'Z̷̢̨̧̛̮̣̫̱̩̹̜̦͔̱̫̪̮̼̹͎̫͖̺͇̼̱̺̭̤̫̙̲̟͉̬̲̱̣̗͓̺̻̗͕̪̺̪̦̞̖̰̪̪̣̈́̓̽͊́̈́̎̾̀̍͐̈́͛̎̅͊̈́̊̈́͌̑̽̇̎̉͂͛̈́̈́̎̌͘̚̕͝͝͝ͅa̴l̵g̸o̷',
    description: 'Maximum chaos Zalgo with extreme diacritical stacking.',
    keywords: ['heavy zalgo', 'extreme glitch text', 'maximum cursed'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        if (char === ' ') return char;
        let result = char;
        for (let i = 0; i < 15; i++) {
          result += ZALGO_UP[Math.floor(Math.random() * ZALGO_UP.length)];
          if (Math.random() > 0.3) result += ZALGO_MID[Math.floor(Math.random() * ZALGO_MID.length)];
          result += ZALGO_DOWN[Math.floor(Math.random() * ZALGO_DOWN.length)];
        }
        return result;
      }).join('');
    },
    related: ['zalgo', 'zalgo-light', 'creepy'],
    faq: [
      { q: 'What is Zalgo heavy?', a: 'Zalgo heavy stacks maximum combining characters for extreme visual distortion.' }
    ]
  },

  // === DECORATED TEXT ===
  {
    id: 'hearts',
    name: 'Hearts',
    slug: 'hearts',
    category: 'decorated',
    example: '♥H♥e♥a♥r♥t♥s♥',
    description: 'Surround your text with love hearts.',
    keywords: ['heart text generator', 'love text', 'texto con corazones', 'heart symbols'],
    transform: (text: string): string => '♥' + text.split('').join('♥') + '♥',
    related: ['sparkles', 'stars', 'flowers'],
    faq: [
      { q: 'How do heart decorations work?', a: 'Hearts are added between each character and at the start and end of your text.' }
    ]
  },
  {
    id: 'sparkles',
    name: 'Sparkles',
    slug: 'sparkles',
    category: 'decorated',
    example: '✨S✨p✨a✨r✨k✨l✨e✨s✨',
    description: 'Add sparkle emojis throughout your text.',
    keywords: ['sparkle text generator', 'glitter text', 'texto brillante', 'aesthetic sparkles'],
    transform: (text: string): string => '✨' + text.split('').join('✨') + '✨',
    related: ['hearts', 'stars', 'aesthetic'],
    faq: [
      { q: 'What are sparkles used for?', a: 'Sparkles add a magical, eye-catching quality popular on Instagram and TikTok.' }
    ]
  },
  {
    id: 'stars',
    name: 'Stars',
    slug: 'stars',
    category: 'decorated',
    example: '★S★t★a★r★s★',
    description: 'Decorate your text with star symbols.',
    keywords: ['star text generator', 'texto con estrellas', 'star symbols'],
    transform: (text: string): string => '★' + text.split('').join('★') + '★',
    related: ['sparkles', 'hearts', 'flowers'],
    faq: [
      { q: 'How do star decorations work?', a: 'Star symbols are inserted between each character in your text.' }
    ]
  },
  {
    id: 'flowers',
    name: 'Flowers',
    slug: 'flowers',
    category: 'decorated',
    example: '❀F❀l❀o❀w❀e❀r❀s❀',
    description: 'Add floral decorations to your text.',
    keywords: ['flower text generator', 'floral text', 'texto con flores'],
    transform: (text: string): string => '❀' + text.split('').join('❀') + '❀',
    related: ['hearts', 'sparkles', 'stars'],
    faq: [
      { q: 'What flower symbol is used?', a: 'We use the Unicode flower symbol ❀ (U+2740) between characters.' }
    ]
  },
  {
    id: 'arrows',
    name: 'Arrows',
    slug: 'arrows',
    category: 'decorated',
    example: '➤A➤r➤r➤o➤w➤s➤',
    description: 'Add arrow symbols throughout your text.',
    keywords: ['arrow text generator', 'arrow symbols'],
    transform: (text: string): string => '➤' + text.split('').join('➤') + '➤',
    related: ['stars', 'brackets', 'lines'],
    faq: [
      { q: 'What are arrow decorations for?', a: 'Arrows add direction and emphasis, great for calls-to-action.' }
    ]
  },

  // === SPECIAL EFFECTS ===
  {
    id: 'ransom',
    name: 'Ransom Note',
    slug: 'ransom',
    category: 'fun',
    example: '𝐑𝒂𝗻𝘀𝓸𝕞',
    description: 'Random mix of styles like a cut-out ransom note.',
    keywords: ['ransom note text', 'random font generator', 'mixed fonts'],
    transform: (text: string): string => {
      const styles = [
        (c: string) => transformWithOffset(c, 0x1D400, 0x1D41A), // Bold Serif
        (c: string) => transformWithOffset(c, 0x1D434, 0x1D44E), // Italic Serif
        (c: string) => transformWithOffset(c, 0x1D5D4, 0x1D5EE), // Bold Sans
        (c: string) => transformWithOffset(c, 0x1D608, 0x1D622), // Italic Sans
        (c: string) => transformWithOffset(c, 0x1D4D0, 0x1D4EA), // Script Bold
        (c: string) => transformWithOffset(c, 0x1D538, 0x1D552), // Double-struck
        (c: string) => transformWithOffset(c, 0x1D504, 0x1D51E), // Fraktur
      ];
      return text.split('').map(char => {
        if (char === ' ') return ' ';
        const randomStyle = styles[Math.floor(Math.random() * styles.length)];
        return randomStyle(char);
      }).join('');
    },
    related: ['zalgo', 'creepy', 'bold'],
    faq: [
      { q: 'What is ransom note text?', a: 'Ransom note randomly applies different font styles to each character, like letters cut from magazines.' },
      { q: 'Is ransom text random each time?', a: 'Yes, each generation produces a unique combination of styles.' }
    ]
  },
  {
    id: 'spaced',
    name: 'Spaced Out',
    slug: 'spaced',
    category: 'aesthetic',
    example: 'S P A C E D',
    description: 'Add spaces between each character for a spread out look.',
    keywords: ['spaced text generator', 'spread out text', 'letter spacing'],
    transform: (text: string): string => text.split('').join(' '),
    related: ['fullwidth', 'aesthetic', 'vaporwave'],
    faq: [
      { q: 'What is spaced text?', a: 'Spaced text simply adds a space between each character for a stretched appearance.' }
    ]
  },
  {
    id: 'parentheses',
    name: 'Parentheses',
    slug: 'parentheses',
    category: 'decorated',
    example: '(P)(a)(r)(e)(n)',
    description: 'Wrap each character in parentheses.',
    keywords: ['parentheses text', 'bracketed text'],
    transform: (text: string): string => text.split('').map(c => c === ' ' ? ' ' : `(${c})`).join(''),
    related: ['brackets', 'curly', 'box'],
    faq: [
      { q: 'How do parentheses work?', a: 'Each character is wrapped in ( and ) symbols.' }
    ]
  },
  {
    id: 'wavy',
    name: 'Wavy',
    slug: 'wavy',
    category: 'decorator',
    example: 'W̃ãṽỹ',
    description: 'Add wavy tilde marks above each character.',
    keywords: ['wavy text generator', 'tilde text', 'squiggly text'],
    transform: (text: string): string => text.split('').map(char => char + '\u0303').join(''),
    related: ['strikethrough', 'underline', 'zalgo'],
    faq: [
      { q: 'How does wavy text work?', a: 'Wavy text adds the combining tilde (U+0303) above each character.' }
    ]
  },
  {
    id: 'dotted',
    name: 'Dotted',
    slug: 'dotted',
    category: 'decorator',
    example: 'Ḋọṭṭėḋ',
    description: 'Add dots above and below characters.',
    keywords: ['dotted text generator', 'dot accent text'],
    transform: (text: string): string => text.split('').map(char => `${char}\u0307\u0323`).join(''),
    related: ['wavy', 'underline', 'strikethrough'],
    faq: [
      { q: 'What is dotted text?', a: 'Dotted text adds combining dot above (U+0307) and dot below (U+0323) to each character.' }
    ]
  },

  // === BRACKETS ===
  {
    id: 'brackets',
    name: 'Brackets',
    slug: 'brackets',
    category: 'decorated',
    example: '【B】【r】【a】【c】【k】【e】【t】【s】',
    description: 'Wrap characters in Japanese-style brackets.',
    keywords: ['bracket text generator', 'japanese brackets'],
    transform: (text: string): string => text.split('').map(c => c === ' ' ? ' ' : `【${c}】`).join(''),
    related: ['parentheses', 'curly', 'square'],
    faq: [
      { q: 'What brackets are used?', a: 'We use Japanese lenticular brackets 【 and 】 (U+3010 and U+3011).' }
    ]
  },
  {
    id: 'curly',
    name: 'Curly Brackets',
    slug: 'curly',
    category: 'decorated',
    example: '『C』『u』『r』『l』『y』',
    description: 'Wrap characters in decorative curly brackets.',
    keywords: ['curly bracket text', 'fancy brackets'],
    transform: (text: string): string => text.split('').map(c => c === ' ' ? ' ' : `『${c}』`).join(''),
    related: ['brackets', 'parentheses', 'box'],
    faq: [
      { q: 'What are these brackets?', a: 'These are CJK corner brackets 『 and 』 commonly used in East Asian typography.' }
    ]
  },

  // === SCI-FI / FANTASY STYLES ===
  {
    id: 'runic',
    name: 'Runic',
    slug: 'runic',
    category: 'fun',
    example: 'ᚱᚢᚾᛁᚲ',
    description: 'Transform text into ancient Viking Elder Futhark runes. Perfect for fantasy, Norse mythology, and sci-fi themes.',
    keywords: ['runic text generator', 'viking runes', 'elder futhark', 'norse runes', 'rune converter', 'fantasy font'],
    transform: (text: string): string => transformWithMap(text, RUNIC_MAP, true),
    related: ['alien', 'fraktur', 'matrix'],
    faq: [
      { q: 'What are runic characters?', a: 'These are Elder Futhark runes, the oldest form of runic alphabets used by Germanic peoples from the 2nd to 8th centuries.' },
      { q: 'Can I use runic text on social media?', a: 'Yes! Runic Unicode characters work on Instagram, Twitter, Discord, and most platforms.' },
      { q: 'Is this real Viking writing?', a: 'These are authentic Elder Futhark rune symbols mapped to the English alphabet for easy conversion.' }
    ]
  },
  {
    id: 'alien',
    name: 'Alien Script',
    slug: 'alien',
    category: 'fun',
    example: 'ⴰⵍⵉⵦⵏ',
    description: 'Mysterious alien-looking geometric script inspired by Tifinagh. Great for sci-fi, extraterrestrial themes, and unique bios.',
    keywords: ['alien text generator', 'alien font', 'sci-fi text', 'extraterrestrial font', 'geometric script', 'tifinagh'],
    transform: (text: string): string => transformWithMap(text, ALIEN_MAP, true),
    related: ['runic', 'matrix', 'cyberpunk'],
    faq: [
      { q: 'What script is this based on?', a: 'This is based on Tifinagh, the script used by Berber/Amazigh peoples of North Africa. Its geometric shapes give an alien appearance.' },
      { q: 'Does alien text work everywhere?', a: 'Most modern platforms support Tifinagh Unicode characters, though some older systems may show boxes.' },
      { q: 'Is this a real language?', a: 'Tifinagh is a real writing system, but we map it to English letters for a decorative alien effect.' }
    ]
  },
  {
    id: 'matrix',
    name: 'Matrix / Hacker',
    slug: 'matrix',
    category: 'fun',
    example: '₥∀┼Я¡×',
    description: 'Hacker-style text with symbol substitutions. Perfect for cyberpunk aesthetics, tech themes, and l33t speak vibes.',
    keywords: ['matrix text generator', 'hacker font', 'cyberpunk text', 'leet speak', 'l33t text', 'tech font', 'coding aesthetic'],
    transform: (text: string): string => transformWithMap(text, MATRIX_MAP, true),
    related: ['alien', 'cyberpunk', 'zalgo'],
    faq: [
      { q: 'What is Matrix text?', a: 'Matrix text replaces letters with similar-looking symbols and currency signs for a hacker/tech aesthetic.' },
      { q: 'Is this the same as l33t speak?', a: 'Similar concept! Both substitute characters, but Matrix style uses more Unicode symbols for a cleaner look.' },
      { q: 'Will this work in usernames?', a: 'Many platforms accept these characters, but some games may restrict special Unicode in names.' }
    ]
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    slug: 'cyberpunk',
    category: 'fun',
    example: '【ＣＹＢＥＲ】',
    description: 'Futuristic cyberpunk style with fullwidth characters and Japanese brackets. Perfect for gaming, tech, and neon aesthetics.',
    keywords: ['cyberpunk text generator', 'cyberpunk font', 'futuristic text', 'neon text', 'gaming font', 'vaporwave'],
    transform: (text: string): string => {
      const fullwidth = text.split('').map(char => {
        const code = char.charCodeAt(0);
        if (code >= 33 && code <= 126) {
          return String.fromCharCode(code + 0xFEE0);
        }
        return char;
      }).join('');
      return `【${fullwidth}】`;
    },
    related: ['matrix', 'fullwidth', 'brackets'],
    faq: [
      { q: 'What makes text look cyberpunk?', a: 'Cyberpunk style uses fullwidth characters (wider spacing) wrapped in Japanese lenticular brackets for a futuristic look.' },
      { q: 'Can I use this for gaming names?', a: 'Yes! Cyberpunk text is popular for Discord, gaming profiles, and social media bios.' },
      { q: 'Why fullwidth characters?', a: 'Fullwidth characters are used in East Asian typography and give text a distinctive, tech-forward appearance.' }
    ]
  },
  {
    id: 'glitch-horror',
    name: 'Glitch Horror',
    slug: 'glitch-horror',
    category: 'fun',
    example: 'ɥ̷̢͝o̶̜͝ɹ̵͜͝ɹ̴̛͜o̵͝ɥ',
    description: 'Creepy upside-down text with glitch effects. Perfect for horror themes, dark aesthetics, and spooky content.',
    keywords: ['horror text generator', 'creepy text', 'glitch horror', 'scary font', 'dark text', 'spooky text'],
    transform: (text: string): string => {
      // First flip upside down
      const flipped = text.split('').map(char => UPSIDE_DOWN_MAP[char] || char).reverse().join('');
      // Then add light zalgo
      return flipped.split('').map(char => {
        if (char === ' ') return char;
        let result = char;
        if (Math.random() > 0.4) result += ZALGO_UP[Math.floor(Math.random() * ZALGO_UP.length)];
        if (Math.random() > 0.6) result += ZALGO_DOWN[Math.floor(Math.random() * ZALGO_DOWN.length)];
        return result;
      }).join('');
    },
    related: ['upside-down', 'zalgo', 'zalgo-heavy'],
    faq: [
      { q: 'What is glitch horror text?', a: 'Glitch horror combines upside-down text with zalgo-style corrupted marks for a creepy, unsettling effect.' },
      { q: 'Is this like Stranger Things text?', a: 'It creates a similar dark, otherworldly vibe using upside-down characters and glitch effects.' },
      { q: 'Can I adjust the glitch intensity?', a: 'This style has balanced intensity. For more or less glitch, try our Zalgo Light or Zalgo Heavy styles.' }
    ]
  },

  // === NEW STYLES - December 2024 ===

  // Missing Basics
  {
    id: 'leet',
    name: 'Leet Speak',
    slug: 'leet',
    category: 'fun',
    example: 'l337 5p34k',
    description: 'Classic l33t speak/1337 text that replaces letters with numbers. Popular in gaming, hacker culture, and internet memes.',
    keywords: ['leet speak generator', 'l33t text', '1337 translator', 'hacker text', 'gaming text', 'leet converter'],
    transform: (text: string): string => {
      return text.split('').map(char => LEET_MAP[char] || char).join('');
    },
    related: ['matrix', 'cyberpunk', 'random-caps'],
    faq: [
      { q: 'What is leet speak?', a: 'Leet speak (1337) originated in the 1980s hacker culture, replacing letters with similar-looking numbers and symbols.' },
      { q: 'Where is leet speak used?', a: 'Popular in gaming communities, meme culture, and for creating unique usernames that look "hacker-style".' },
      { q: 'Can I use leet speak for usernames?', a: 'Yes! Leet speak is great for gaming usernames, social media handles, and anywhere you want a tech aesthetic.' }
    ]
  },
  {
    id: 'random-caps',
    name: 'Random Caps',
    slug: 'random-caps',
    category: 'fun',
    example: 'RaNdOm CaPs',
    description: 'Randomly alternate between uppercase and lowercase letters. Perfect for the Spongebob mocking meme and sarcastic text.',
    keywords: ['random caps generator', 'spongebob text', 'mocking text', 'sarcastic text generator', 'alternating caps'],
    transform: (text: string): string => {
      return text.split('').map(char => {
        if (Math.random() > 0.5) {
          return char.toUpperCase();
        }
        return char.toLowerCase();
      }).join('');
    },
    related: ['leet', 'ransom', 'zalgo'],
    faq: [
      { q: 'What is random caps text?', a: 'Random caps randomly alternates between uppercase and lowercase, creating the "mocking Spongebob" meme effect.' },
      { q: 'Is this the Spongebob meme text?', a: 'Yes! This style became famous from the "Mocking Spongebob" meme where mixed caps implies sarcasm.' },
      { q: 'Is the result different each time?', a: 'Yes, each generation produces a unique random pattern of uppercase and lowercase letters.' }
    ]
  },
  {
    id: 'overline',
    name: 'Overline',
    slug: 'overline',
    category: 'decorator',
    example: 'O̅v̅e̅r̅l̅i̅n̅e̅',
    description: 'Add a line above each character for mathematical notation or emphasis.',
    keywords: ['overline text generator', 'line above text', 'macron text', 'mathematical notation'],
    transform: (text: string): string => text.split('').map(char => char + '\u0305').join(''),
    related: ['underline', 'strikethrough', 'double-underline'],
    faq: [
      { q: 'What is overline text?', a: 'Overline adds a combining overline (U+0305) above each character, commonly used in math and for emphasis.' },
      { q: 'How is overline different from underline?', a: 'Overline places the line above letters, while underline places it below. Both use Unicode combining characters.' }
    ]
  },

  // Unique Converters
  {
    id: 'morse',
    name: 'Morse Code',
    slug: 'morse',
    category: 'fun',
    example: '.... . .-.. .-.. ---',
    description: 'Convert your text to Morse code dots and dashes. Great for secret messages, retro aesthetics, and learning Morse.',
    keywords: ['morse code translator', 'morse code generator', 'text to morse', 'morse converter', 'dot dash text'],
    transform: (text: string): string => {
      return text.split('').map(char => MORSE_MAP[char] || char).join(' ');
    },
    related: ['braille', 'binary', 'matrix'],
    faq: [
      { q: 'What is Morse code?', a: 'Morse code represents letters as sequences of dots (.) and dashes (-), invented by Samuel Morse in the 1830s for telegraph communication.' },
      { q: 'Can this Morse code be decoded?', a: 'Yes! Standard Morse code can be decoded by any Morse translator. Spaces separate letters, slashes separate words.' },
      { q: 'Where is Morse code used today?', a: 'Still used in aviation, amateur radio, accessibility tools, and as a fun retro aesthetic for text styling.' }
    ]
  },
  {
    id: 'braille',
    name: 'Braille',
    slug: 'braille',
    category: 'fun',
    example: '⠓⠑⠇⠇⠕',
    description: 'Convert text to Unicode Braille patterns. A unique visual style that also represents actual Braille characters.',
    keywords: ['braille text generator', 'braille converter', 'braille translator', 'dot pattern text', 'accessibility text'],
    transform: (text: string): string => {
      return text.split('').map(char => BRAILLE_MAP[char] || char).join('');
    },
    related: ['morse', 'matrix', 'runic'],
    faq: [
      { q: 'What is Braille text?', a: 'Braille is a tactile writing system for visually impaired people. This uses Unicode Braille patterns for a visual aesthetic.' },
      { q: 'Is this real Braille?', a: 'Yes! These are actual Unicode Braille characters that follow Grade 1 Braille standards for letters.' },
      { q: 'Can visually impaired people read this?', a: 'Screen readers can interpret these Braille Unicode characters, though this is primarily for visual styling.' }
    ]
  },

  // Emoji Separators
  {
    id: 'fire',
    name: 'Fire',
    slug: 'fire',
    category: 'decorated',
    example: '🔥F🔥I🔥R🔥E🔥',
    description: 'Add fire emojis between each character for a hot, trending look. Perfect for hyping up your posts.',
    keywords: ['fire text generator', 'flame text', 'hot text', 'fire emoji text', 'lit text'],
    transform: (text: string): string => '🔥' + text.split('').join('🔥') + '🔥',
    related: ['lightning', 'sparkles', 'stars'],
    faq: [
      { q: 'What is fire text for?', a: 'Fire text adds 🔥 emojis to make your text look "lit" or trending - popular on social media.' },
      { q: 'Does fire text work on Instagram?', a: 'Yes! Fire emojis work on all major platforms including Instagram, TikTok, Twitter, and Discord.' }
    ]
  },
  {
    id: 'ice',
    name: 'Ice',
    slug: 'ice',
    category: 'decorated',
    example: '❄I❄C❄E❄',
    description: 'Add snowflake symbols between each character for a cool, frosty aesthetic.',
    keywords: ['ice text generator', 'snowflake text', 'frozen text', 'winter text', 'cold text'],
    transform: (text: string): string => '❄' + text.split('').join('❄') + '❄',
    related: ['fire', 'sparkles', 'stars'],
    faq: [
      { q: 'What is ice text for?', a: 'Ice text adds ❄ snowflakes for a cool, frosty aesthetic - great for winter themes or "cold" vibes.' },
      { q: 'Is this good for winter posts?', a: 'Perfect for winter holidays, snow content, or when you want a "chill" aesthetic.' }
    ]
  },
  {
    id: 'lightning',
    name: 'Lightning',
    slug: 'lightning',
    category: 'decorated',
    example: '⚡L⚡I⚡G⚡H⚡T⚡',
    description: 'Add lightning bolt symbols between each character for an electric, energetic look.',
    keywords: ['lightning text generator', 'bolt text', 'electric text', 'power text', 'energy text'],
    transform: (text: string): string => '⚡' + text.split('').join('⚡') + '⚡',
    related: ['fire', 'sparkles', 'stars'],
    faq: [
      { q: 'What is lightning text for?', a: 'Lightning text adds ⚡ bolts for an electric, high-energy aesthetic - great for sports, gaming, and action content.' },
      { q: 'Does this work for gaming names?', a: 'Yes! Lightning bolt text is popular for gaming usernames and esports content.' }
    ]
  },
  {
    id: 'crown',
    name: 'Crown',
    slug: 'crown',
    category: 'decorated',
    example: '👑 Crown 👑',
    description: 'Wrap your text with crown emojis for a royal, boss-like appearance.',
    keywords: ['crown text generator', 'king text', 'queen text', 'royal text', 'boss text'],
    transform: (text: string): string => '👑 ' + text + ' 👑',
    related: ['sparkles', 'stars', 'wings'],
    faq: [
      { q: 'What is crown text for?', a: 'Crown text adds 👑 emojis to give your text a royal, "king/queen" vibe - popular for bios and usernames.' },
      { q: 'Is this good for Instagram bios?', a: 'Perfect for Instagram, TikTok bios, and anywhere you want to show confidence or status.' }
    ]
  },
  {
    id: 'skull',
    name: 'Skull',
    slug: 'skull',
    category: 'decorated',
    example: '☠S☠K☠U☠L☠L☠',
    description: 'Add skull symbols between each character for a dark, edgy aesthetic.',
    keywords: ['skull text generator', 'death text', 'dark text', 'edgy text', 'goth text'],
    transform: (text: string): string => '☠' + text.split('').join('☠') + '☠',
    related: ['glitch-horror', 'zalgo', 'fire'],
    faq: [
      { q: 'What is skull text for?', a: 'Skull text adds ☠ symbols for a dark, edgy aesthetic - popular in gaming, metal, and goth communities.' },
      { q: 'Is this good for gaming names?', a: 'Yes! Skull text is popular for gaming usernames, especially in FPS and battle royale games.' }
    ]
  },

  // Wrappers
  {
    id: 'wings',
    name: 'Wings',
    slug: 'wings',
    category: 'decorated',
    example: '༺ Wings ༻',
    description: 'Wrap your text with decorative Tibetan wing-like brackets for an elegant, mystical look.',
    keywords: ['wings text generator', 'decorative text', 'fancy brackets', 'tibetan text', 'elegant wrapper'],
    transform: (text: string): string => '༺ ' + text + ' ༻',
    related: ['crown', 'brackets', 'curly'],
    faq: [
      { q: 'What are wing brackets?', a: 'These are Tibetan ornamental characters ༺ and ༻ that look like decorative wings around your text.' },
      { q: 'Does this work on all platforms?', a: 'Most modern platforms support these Unicode characters, though some older systems may show boxes.' }
    ]
  },
  {
    id: 'neon',
    name: 'Neon',
    slug: 'neon',
    category: 'aesthetic',
    example: '》N《》E《》O《》N《',
    description: 'Add angular Asian-style brackets between each character for a neon sign, cyberpunk aesthetic.',
    keywords: ['neon text generator', 'cyberpunk text', 'aesthetic text', 'vaporwave text', 'glowing text'],
    transform: (text: string): string => '》' + text.split('').map(c => c === ' ' ? '《 》' : c).join('《》') + '《',
    related: ['cyberpunk', 'fullwidth', 'brackets'],
    faq: [
      { q: 'What is neon text?', a: 'Neon text uses 》 and 《 angle brackets to create a glowing, neon sign effect popular in cyberpunk aesthetics.' },
      { q: 'Is this the vaporwave style?', a: 'Similar vibe! This works great alongside fullwidth text for that 80s/90s retro-futuristic aesthetic.' }
    ]
  }
];

// Get style by ID or slug
export const getStyleById = (id: string): TextStyle | undefined => TEXT_STYLES.find(s => s.id === id);
export const getStyleBySlug = (slug: string): TextStyle | undefined => TEXT_STYLES.find(s => s.slug === slug);

// Get styles by category
export const getStylesByCategory = (category: StyleCategory): TextStyle[] => TEXT_STYLES.filter(s => s.category === category);

// Get all categories
export const getCategories = (): StyleCategory[] => [...new Set(TEXT_STYLES.map(s => s.category))];

// Get related styles
export const getRelatedStyles = (styleId: string): TextStyle[] => {
  const style = getStyleById(styleId);
  if (!style) return [];
  return style.related.map(id => getStyleById(id)).filter((s): s is TextStyle => s !== undefined);
};

export default TEXT_STYLES;
